import * as Yup from 'yup'
import { COUNTRY_ID } from './fields'
import {
  ageFields,
  AVERAGE_DECREASED_ANXIETY_RATING_AFTER,
  AVERAGE_DECREASED_ANXIETY_RATING_BEFORE,
  AVERAGE_HAPPINESS_RATING_AFTER,
  AVERAGE_HAPPINESS_RATING_BEFORE,
  AVERAGE_LIFE_SATISFACTION_RATING_AFTER,
  AVERAGE_LIFE_SATISFACTION_RATING_BEFORE,
  AVERAGE_PURPOSE_OF_LIFE_RATING_AFTER,
  AVERAGE_PURPOSE_OF_LIFE_RATING_BEFORE,
  ethnicityFields,
  imdFields
} from './stakeholdersFields'

export const imdPercentageValidation = Yup.object().test(
  'imd-percentage-validation',
  'IMD percentages must be valid',
  function (value) {
    if (!value) {
      return this.createError({
        message: 'IMD percentages are required',
        path: this.path
      })
    }

    // Get all IMD field values (excluding unknown which is auto-calculated)
    const imdFieldKeys = Object.keys(imdFields).filter(key => key !== 'unknown')
    const percentages = imdFieldKeys
      .map(key => {
        const v = value[imdFields[key]]
        return typeof v === 'string' ? parseFloat(v) : v
      })
      .filter(v => typeof v === 'number' && !isNaN(v))

    // Check each percentage is in range [0, 100]
    const allInRange = percentages.every(p => p >= 0 && p <= 100)
    if (!allInRange) {
      return this.createError({
        message: 'Each IMD percentage must be between 0 and 100',
        path: this.path
      })
    }

    // Check sum of imd_1 to imd_10 is <= 100
    // The unknown field is auto-calculated as (100 - sum) to ensure total = 100
    const sum = percentages.reduce((acc, val) => acc + val, 0)
    if (sum > 100) {
      return this.createError({
        message: `IMD percentages (1-10) must sum to 100 or less (current sum: ${sum})`,
        path: this.path
      })
    }

    return true
  }
)
export const activityLevelsValidation = Yup.object().test(
  'activity-levels-sum',
  'Activity levels percentages are required and must sum to 100',
  function (value) {
    if (!value) {
      return this.createError({
        message: 'Activity levels percentages are required',
        path: this.path
      })
    }

    // Convert string values to numbers and filter out invalid values
    const percentages = Object.values(value)
      .map(v => (typeof v === 'string' ? parseFloat(v) : v))
      .filter(v => typeof v === 'number' && !isNaN(v))

    // Check each percentage is in range [0, 100]
    const allInRange = percentages.every(p => p >= 0 && p <= 100)
    if (!allInRange) {
      return this.createError({
        message: 'Each activity level percentage must be between 0 and 100',
        path: this.path
      })
    }

    // Check sum equals 100 (required, can't be 0)
    const sum = percentages.reduce((acc, val) => acc + val, 0)
    if (sum !== 100) {
      return this.createError({
        message:
          sum === 0
            ? 'Activity levels percentages are required'
            : `Activity levels percentages must sum to 100 (current sum: ${sum})`,
        path: this.path
      })
    }

    return true
  }
)
export const agePercentageValidation = Yup.object().test(
  'age-percentage-sum',
  'Age distribution percentages are required and must sum to 100',
  function (value) {
    if (!value) {
      return this.createError({
        message: 'Age distribution percentages are required',
        path: this.path
      })
    }

    // Convert string values to numbers and filter out invalid values
    // Note: age fields have .total property
    const percentages = Object.values(ageFields)
      .map(field => {
        const v = value[field]?.total
        return typeof v === 'string' ? parseFloat(v) : v
      })
      .filter(v => typeof v === 'number' && !isNaN(v))

    // Check each percentage is in range [0, 100]
    const allInRange = percentages.every(p => p >= 0 && p <= 100)
    if (!allInRange) {
      return this.createError({
        message: 'Each age distribution percentage must be between 0 and 100',
        path: this.path
      })
    }

    // Check sum equals 100 (required, can't be 0)
    const sum = percentages.reduce((acc, val) => acc + val, 0)
    if (sum !== 100) {
      return this.createError({
        message:
          sum === 0
            ? 'Age distribution percentages are required'
            : `Age distribution percentages must sum to 100 (current sum: ${sum})`,
        path: this.path
      })
    }

    return true
  }
)
export const genderPercentageValidation = Yup.object().test(
  'gender-percentage-sum',
  'Gender percentages are required and must sum to 100',
  function (value) {
    if (!value) {
      return this.createError({
        message: 'Gender percentages are required',
        path: this.path
      })
    }

    // Convert string values to numbers and filter out invalid values
    const percentages = Object.values(value)
      .map(v => (typeof v === 'string' ? parseFloat(v) : v))
      .filter(v => typeof v === 'number' && !isNaN(v))

    // Check each percentage is in range [0, 100]
    const allInRange = percentages.every(p => p >= 0 && p <= 100)
    if (!allInRange) {
      return this.createError({
        message: 'Each gender percentage must be between 0 and 100',
        path: this.path
      })
    }

    // Check sum equals 100 (required, can't be 0)
    const sum = percentages.reduce((acc, val) => acc + val, 0)
    if (sum !== 100) {
      return this.createError({
        message:
          sum === 0 ? 'Gender percentages are required' : `Gender percentages must sum to 100 (current sum: ${sum})`,
        path: this.path
      })
    }

    return true
  }
)
export const ethnicityPercentageValidation = Yup.object().test(
  'ethnicity-percentage-sum',
  'Ethnicity percentages are required and must sum to 100',
  function (value) {
    if (!value) {
      return this.createError({
        message: 'Ethnicity percentages are required',
        path: this.path
      })
    }

    // Get country from root form values
    const country = this.from[this.from.length - 1].value[COUNTRY_ID]

    // Determine which fields to validate based on country
    const saudiArabiaFields = [
      ethnicityFields.local,
      ethnicityFields.non_local,
      ethnicityFields.international,
      ethnicityFields.unknown
    ]
    const regularFields = [
      ethnicityFields.white,
      ethnicityFields.asian,
      ethnicityFields.black,
      ethnicityFields.mixed,
      ethnicityFields.unknown
    ]

    const fieldsToValidate = country === 'SAU' ? saudiArabiaFields : regularFields

    // Only get values for the relevant fields
    const percentages = fieldsToValidate
      .map(field => {
        const v = value[field]
        return typeof v === 'string' ? parseFloat(v) : v
      })
      .filter(v => typeof v === 'number' && !isNaN(v))

    // Check each percentage is in range [0, 100]
    const allInRange = percentages.every(p => p >= 0 && p <= 100)
    if (!allInRange) {
      return this.createError({
        message: 'Each ethnicity percentage must be between 0 and 100',
        path: this.path
      })
    }

    // Check sum equals 100 (required, can't be 0)
    const sum = percentages.reduce((acc, val) => acc + val, 0)
    if (sum !== 100) {
      return this.createError({
        message:
          sum === 0
            ? 'Ethnicity percentages are required'
            : `Ethnicity percentages must sum to 100 (current sum: ${sum})`,
        path: this.path
      })
    }

    return true
  }
)
// Wellbeing rating validation (range [0, 10])
const wellbeingRatingValidation = Yup.number()
  .min(0, 'must be greater than or equal to 0')
  .max(10, 'must be less than or equal to 10')
export const wellbeingRatingsValidation = {
  [AVERAGE_LIFE_SATISFACTION_RATING_BEFORE]: wellbeingRatingValidation,
  [AVERAGE_LIFE_SATISFACTION_RATING_AFTER]: wellbeingRatingValidation,
  [AVERAGE_PURPOSE_OF_LIFE_RATING_BEFORE]: wellbeingRatingValidation,
  [AVERAGE_PURPOSE_OF_LIFE_RATING_AFTER]: wellbeingRatingValidation,
  [AVERAGE_HAPPINESS_RATING_BEFORE]: wellbeingRatingValidation,
  [AVERAGE_HAPPINESS_RATING_AFTER]: wellbeingRatingValidation,
  [AVERAGE_DECREASED_ANXIETY_RATING_BEFORE]: wellbeingRatingValidation,
  [AVERAGE_DECREASED_ANXIETY_RATING_AFTER]: wellbeingRatingValidation
}
